import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { isSignInWithEmailLink } from 'firebase/auth';
import { firstValueFrom } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserService } from '../services/user-service/user.service';

const EMAIL_STORAGE_KEY = 'portfolioViewerEmailForSignIn';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.sass'
})
export class LoginComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(Auth);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  errorMessage = '';
  infoMessage = '';
  busy = false;
  completingEmailLink = false;

  emailForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });

  private returnUrl = '/portfolio';

  async ngOnInit(): Promise<void> {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '/portfolio';
    const href = typeof window !== 'undefined' ? window.location.href : '';

    if (href && isSignInWithEmailLink(this.auth, href)) {
      this.completingEmailLink = true;
      this.busy = true;
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem(EMAIL_STORAGE_KEY) : null;
      const email = stored ?? '';
      if (!email) {
        this.busy = false;
        this.completingEmailLink = false;
        this.errorMessage =
          'Open this link on the same browser where you requested it, or enter your email below and request a new link.';
        return;
      }
      try {
        await firstValueFrom(this.userService.completeViewerEmailLink(email, href));
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(EMAIL_STORAGE_KEY);
        }
        await this.router.navigateByUrl(this.returnUrl);
      } catch (e: unknown) {
        this.errorMessage = e instanceof Error ? e.message : 'Could not complete sign-in.';
      } finally {
        this.busy = false;
        this.completingEmailLink = false;
      }
    }
  }

  async signInWithGoogle(): Promise<void> {
    this.errorMessage = '';
    this.infoMessage = '';
    this.busy = true;
    try {
      await firstValueFrom(this.userService.signInWithGoogle());
      await this.router.navigateByUrl(this.returnUrl);
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : 'Google sign-in failed.';
    } finally {
      this.busy = false;
    }
  }

  async sendEmailLink(): Promise<void> {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }
    const email = this.emailForm.controls.email.value?.trim() ?? '';
    this.errorMessage = '';
    this.infoMessage = '';
    this.busy = true;
    const continueUrl =
      typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
      }
      await firstValueFrom(this.userService.sendViewerEmailLink(email, continueUrl));
      this.infoMessage =
        'Check your email for a sign-in link. After signing in you can view private projects while this session stays active.';
    } catch (e: unknown) {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      }
      this.errorMessage = e instanceof Error ? e.message : 'Could not send email.';
    } finally {
      this.busy = false;
    }
  }
}
